import { Component, Input, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveOffcanvas, NgbModal, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmationPopupComponent } from 'src/app/share/popups/confirmation-popup/confirmation-popup.component';
import { ExpenseService } from 'src/app/share/services/expense/expense.service';
import { PaymentAccountsService } from 'src/app/share/services/payment-account/payment-accounts.service';
import { StorageService } from 'src/app/share/services/storage-service/storage.service';
import { StorageKeys } from 'src/app/share/enums/storage.enum';
import dayjs from 'dayjs';

@Component({
  selector: 'app-expense-detail',
  templateUrl: './expense-detail.component.html',
  styleUrls: ['./expense-detail.component.scss'],
  standalone: false
})
export class ExpenseDetailComponent implements OnInit {
  @Input() mode: 'ADD' | 'VIEW' | 'EDIT' = 'ADD';
  @Input() expenseData: any = null;
  @Input() branches: any[] = [];
  @Input() isBranchVisible: boolean = false;

  expenseForm!: FormGroup;
  @ViewChild('offcanvasBody') offcanvasBody!: ElementRef;
  
  categoriesObj = {
    page: 0,
    loader: false,
    end: false
  };
  allCategories: any[] = [];
  pillCategories: any[] = [];
  dropdownCategories: any[] = [];
  
  paymentAccounts: any[] = [];
  paymentModes = [
    { label: 'Cash', key: 'Cash' },
    { label: 'UPI', key: 'UPI' },
    { label: 'Bank Transfer', key: 'BankTransfer' },
    { label: 'Card', key: 'Card' },
    { label: 'Bajaj Finance', key: 'BajajFinance' }
  ];

  userDetails: any;
  
  saveLoader: boolean = false;
  deleteLoader: boolean = false;
  formErrorMessage: string = '';
  maxDate!: NgbDateStruct;
  selectedNgbDate!: NgbDateStruct;

  constructor(
    public readonly activeOffcanvas: NgbActiveOffcanvas,
    private readonly fb: FormBuilder,
    private readonly expenseService: ExpenseService,
    private readonly paymentAccountsService: PaymentAccountsService,
    private readonly storageService: StorageService,
    private readonly modalService: NgbModal
  ) { }

  ngOnInit(): void {
    this.userDetails = this.storageService.getDataFromLocalStorage(StorageKeys.SELF_DETAILS);

    const today = new Date();
    this.maxDate = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate()
    };

    this.initForm();
    this.getCategories();
  }

  getCategories() {
    this.categoriesObj.loader = true;
    this.expenseService.getExpenseCategory({ page: this.categoriesObj.page }).subscribe({
      next: (res: any) => {
        let categories = res.data || [];
        this.categoriesObj.loader = false;
        if (categories?.length === 0) {
          this.categoriesObj.end = true;
        } else {
          const existingIds = new Set(this.allCategories.map((c: any) => c._id));
          const newCategories = categories.filter((c: any) => c?._id && !existingIds.has(c._id));
          this.allCategories = [...this.allCategories, ...newCategories];
          this.processCategories();
        }
      },
      error: () => {
        this.categoriesObj.loader = false;
      }
    });
  }

  scrollCategories() {
    if (this.categoriesObj?.loader || this.categoriesObj?.end) return;
    this.categoriesObj.page++;
    this.getCategories();
  }

  initForm() {
    this.expenseForm = this.fb.group({
      amount: [null, [Validators.required, Validators.min(1)]],
      category: [null, Validators.required],
      paymentMode: ['Cash', Validators.required],
      paymentAccount: [null, Validators.required],
      company: [this.isBranchVisible ? null : this.userDetails?.company, Validators.required],
      transactionDate: [dayjs().format('YYYY-MM-DD'), Validators.required],
      description: ['', Validators.required]
    });

    this.updateNgbDate(this.expenseForm.get('transactionDate')?.value);

    this.expenseForm.get('company')?.valueChanges.subscribe(() => {
      if (this.expenseForm.get('company')?.dirty) {
        this.expenseForm.get('paymentAccount')?.setValue(null);
      }
      this.fetchPaymentAccounts();
    });
    this.expenseForm.get('paymentMode')?.valueChanges.subscribe(() => {
      if (this.expenseForm.get('paymentMode')?.dirty) {
        this.expenseForm.get('paymentAccount')?.setValue(null);
      }
      this.fetchPaymentAccounts();
    });

    this.expenseForm.get('description')?.valueChanges.subscribe((val: string) => {
      if (val && val.length > 30) {
        this.expenseForm.get('description')?.setValue(val.substring(0, 30), { emitEvent: false });
      }
    });

    if (this.mode !== 'ADD' && this.expenseData) {
      this.patchForm();
      this.fetchPaymentAccounts();
    } else {
      this.fetchPaymentAccounts();
    }
    
    if (this.mode === 'VIEW') {
      this.expenseForm.disable();
    }
  }

  patchForm() {
    this.expenseForm.patchValue({
      amount: this.expenseData.amount,
      category: this.expenseData.category?._id ,
      paymentMode: this.expenseData.paymentMode,
      paymentAccount: this.expenseData.paymentAccount?._id,
      company: this.expenseData.company?._id || this.expenseData.company,
      transactionDate: dayjs(this.expenseData.transactionDate ).format('YYYY-MM-DD'),
      description: this.expenseData.description
    }, { emitEvent: false });
    this.updateNgbDate(this.expenseForm.get('transactionDate')?.value);
  }

  processCategories() {
    let categoriesToProcess = [...this.allCategories];
    
    if (this.mode === 'ADD') {
      categoriesToProcess = categoriesToProcess.filter(c => c.status === 'Active');
    }

    const currentCatId = this.expenseForm?.get('category')?.value || this.expenseData?.category?._id;

    if (currentCatId) {
      const currentCatIndex = categoriesToProcess.findIndex(c => c._id === currentCatId);
      if (currentCatIndex === -1) {
        // Selected category is not in the processed list (e.g. inactive or custom). Append it as the 5th pill
        const catObj = this.allCategories.find(c => c._id === currentCatId) || this.expenseData?.category;
        if (catObj) {
          const firstFour = categoriesToProcess.slice(0, 4);
          this.pillCategories = [...firstFour, catObj];
          this.dropdownCategories = categoriesToProcess.slice(4);
        } else {
          this.pillCategories = categoriesToProcess.slice(0, 5);
          this.dropdownCategories = categoriesToProcess.slice(5);
        }
      } else if (currentCatIndex < 5) {
        this.pillCategories = categoriesToProcess.slice(0, 5);
        this.dropdownCategories = categoriesToProcess.slice(5);
      } else {
        // Selected category is beyond index 4. Show first 4 + selected category as the 5th pill
        const firstFour = categoriesToProcess.slice(0, 4);
        const selectedCat = categoriesToProcess[currentCatIndex];
        this.pillCategories = [...firstFour, selectedCat];
        // Remaining categories in dropdown, excluding the selected one
        this.dropdownCategories = categoriesToProcess.slice(4).filter(c => c._id !== currentCatId);
      }
    } else {
      this.pillCategories = categoriesToProcess.slice(0, 5);
      this.dropdownCategories = categoriesToProcess.slice(5);
    }
  }

  getDropdownCategoryValue(): string | null {
    const selectedId = this.expenseForm?.get('category')?.value;
    if (!selectedId) return null;
    const isPill = this.pillCategories.some(c => c._id === selectedId);
    if (isPill) return null;
    return selectedId;
  }

  onDropdownCategoryChange(catId: string | null) {
    if (catId) {
      this.expenseForm.get('category')?.setValue(catId);
    } else {
      this.expenseForm.get('category')?.setValue(null);
    }
  }

  fetchPaymentAccounts() {
    const company = this.expenseForm.get('company')?.value;
    const paymentMode = this.expenseForm.get('paymentMode')?.value;
    
    if (!company || !paymentMode) {
      this.paymentAccounts = [];
      return;
    }

    this.paymentAccountsService.getCompanyPaymentAccount({
      company: company, paymentType: paymentMode,
      page: 0, status: true, sort:'_id', sortDir: 1
    }).subscribe({
      next: (res: any) => {
        this.paymentAccounts = res.data || [];
        
        // Ensure that if editing/viewing, the active payment account is appended if missing
        const currentAccId = this.expenseForm.get('paymentAccount')?.value;
        if (currentAccId && this.expenseData?.paymentAccount && this.expenseData.paymentAccount._id === currentAccId) {
          const exists = this.paymentAccounts.some(a => a._id === currentAccId);
          if (!exists) {
            this.paymentAccounts.unshift(this.expenseData.paymentAccount);
          }
        }

        if (!this.expenseForm.get('paymentAccount')?.value) {
          const defaultAcc = this.paymentAccounts.find(a => a.default);
          if (defaultAcc) {
            this.expenseForm.get('paymentAccount')?.setValue(defaultAcc._id);
          }
        }
      }
    });
  }

  selectCategory(catId: string) {
    if (this.mode === 'VIEW') return;
    this.expenseForm.get('category')?.setValue(catId);
  }

  getBadgeStyle(category: any, isSelected: boolean) {
    if (!category?.color) return {};
    if (isSelected) {
      return { 
        'background-color': category.color + '33', 
        'color': category.color,
        'border': `2.5px solid ${category.color}`,
        'font-weight': '700',
        'padding': '6px 14px',
        'transform': 'scale(1.06)'
      };
    }
    return { 
      'background-color': category.color + '1A', 
      'color': category.color,
      'border': `1px solid ${category.color}4D`,
      'padding': '4px 10px',
      'font-weight': '500',
      'transform': 'none'
    };
  }

  onDateSelect(date: NgbDateStruct) {
    if (date) {
      const dateStr = `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
      this.expenseForm.get('transactionDate')?.setValue(dateStr);
      this.selectedNgbDate = date;
    }
  }

  updateNgbDate(dateStr: string) {
    if (dateStr?.split('-').length === 3) {
      const parts = dateStr.split('-');
      this.selectedNgbDate = {
        year: Number.parseInt(parts[0], 10),
        month: Number.parseInt(parts[1], 10),
        day: Number.parseInt(parts[2], 10)
      };
    } else {
      const today = new Date();
      this.selectedNgbDate = {
        year: today.getFullYear(),
        month: today.getMonth() + 1,
        day: today.getDate()
      };
    }
  }

  get dateValueFormatted() {
    const val = this.expenseForm.get('transactionDate')?.value;
    return val ? dayjs(val).format('DD MMM YYYY') : 'Select Date';
  }
  
  switchToEdit() {
    this.mode = 'EDIT';
    this.expenseForm.enable();
    this.processCategories(); // Re-process to ensure selected is in pills if needed
  }

  saveExpense() {
    if (this.expenseForm.invalid) {
      this.expenseForm.markAllAsTouched();
      return;
    }
    const payload = this.expenseForm.getRawValue();
    this.saveLoader = true;
    this.expenseForm.disable();
    this.formErrorMessage = '';

    if (this.mode === 'ADD') {
      this.expenseService.createExpense(payload).subscribe({
        next: (res: any) => {
          this.saveLoader = false;
          this.expenseForm.enable();
          this.activeOffcanvas.close('SAVED');
        },
        error: (err: any) => {
          this.saveLoader = false;
          this.expenseForm.enable();
          this.formErrorMessage = err?.error?.error?.message || 'Failed to create expense';
          setTimeout(() => {
            if (this.offcanvasBody) {
              this.offcanvasBody.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }, 100);
        }
      });
    } else if (this.mode === 'EDIT') {
      this.expenseService.updateExpense(this.expenseData._id, payload).subscribe({
        next: (res: any) => {
          this.saveLoader = false;
          this.expenseForm.enable();
          this.activeOffcanvas.close('SAVED');
        },
        error: (err: any) => {
          this.saveLoader = false;
          this.expenseForm.enable();
          this.formErrorMessage = err?.error?.error?.message || 'Failed to update expense';
          setTimeout(() => {
            if (this.offcanvasBody) {
              this.offcanvasBody.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }, 100);
        }
      });
    }
  }

  confirmDelete() {
    const modalRef = this.modalService.open(ConfirmationPopupComponent, {
      centered: true,
      size: 'sm',
      backdrop: 'static',
      windowClass: 'delete-confirm-modal'
    });
    modalRef.componentInstance.title = 'Delete Expense?';
    modalRef.componentInstance.message = `Are you sure you want to permanently remove "${this.expenseData?.description || 'this expense'}" (₹${this.expenseData?.amount})? This action cannot be undone.`;
    modalRef.componentInstance.buttonLabel = 'Delete';
    modalRef.componentInstance.isLoading = false; 
    modalRef.componentInstance.confirm.subscribe(() => {
      modalRef.close();

      this.deleteLoader = true;
      this.expenseForm.disable();
      this.formErrorMessage = '';

      this.expenseService.deleteExpense(this.expenseData._id).subscribe({
        next: () => {
          this.deleteLoader = false;
          this.activeOffcanvas.close('DELETED');
        },
        error: (err: any) => {
          this.deleteLoader = false;
          this.expenseForm.enable();
          const msg = err?.error?.error?.message || 'Failed to delete expense';
          this.formErrorMessage = msg;
          setTimeout(() => {
            if (this.offcanvasBody) {
              this.offcanvasBody.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }, 100);
        }
      });
    });
  }

}
